// src/services/shopService.js
//
// Frontend API wrapper for the mini-shop / online voucher purchase flow.
// =============================================================================
//
// GOALS
// -----
// 1) Keep all Shop-related HTTP calls in one place (service layer).
// 2) Provide domain-friendly function names used by pages/components.
// 3) Align precisely with the CURRENT backend contracts:
//      - GET  /api/shop/products
//      - POST /api/shop/checkout
//      - GET  /api/shop/orders/:id?token=...
// 4) Provide consistent error handling and strong inline documentation.
//
// IMPORTANT BACKEND SECURITY UPDATE (CURRENT APP DEVELOPMENT)
// -----------------------------------------------------------
// The ThankYouPage polling endpoint is PUBLIC and the :id is enumerable.
// Therefore backend requires a token:
//   GET /api/shop/orders/:orderId?token=<publicToken>
//
// The token is currently the Order.session_id generated by the backend using
// crypto.randomUUID() (unguessable). The frontend obtains the token from the
// Thank-You URL query string (redirect from P24 urlReturn).
//
// DO NOT call getOrderStatus(orderId) without token anymore.
// =============================================================================
//
// PATH NOTE
// ---------
// This file assumes apiClient baseURL does NOT already include `/api`,
// hence the `/api/shop/...` prefixes used here.
// If your baseURL already includes `/api`, change these to `/shop/...`.
//
// AUTH / COOKIES NOTE
// -------------------
// The shop endpoints are designed to be PUBLIC and do not require auth cookies.
// However, your global apiClient may attach session headers / cookies.
// Keeping `withCredentials: true` is harmless, but not required.
//
// =============================================================================

import apiClient from "../utils/apiClient";

/* =============================================================================
   Small, reusable helpers
============================================================================= */

/**
 * getErrorMessage(error)
 *
 * Extracts the best possible message from Axios-like errors without leaking
 * sensitive information.
 */
function getErrorMessage(error) {
  return (
    error?.response?.data?.message ||
    error?.message ||
    "Unknown error"
  );
}

/**
 * buildOrderStatusUrl(orderId, token)
 *
 * Backend contract (HARDENED):
 *   GET /api/shop/orders/:orderId?token=<token>
 */
function buildOrderStatusUrl(orderId, token) {
  const id = encodeURIComponent(String(orderId));
  const t = encodeURIComponent(String(token || ""));
  return `/api/shop/orders/${id}?token=${t}`;
}

/* =============================================================================
   1) fetchShopConfig()
   -----------------------------------------------------------------------------
   Loads dynamic voucher configuration from backend.

   Backend:
     GET /api/shop/products    (shopController.listProducts)

   Expected response (example):
     {
       type: "dynamic_voucher",
       name: "Online voucher",
       description: "...",
       minAmount: 50,
       maxAmount: 1000,
       step: 1,
       currency: "PLN",
       layout: {
         defaultTemplateType: "A4_ON_STATIONERY",
         defaultTemplateVersion: "company",
         defaultTheme: "default",
         templateTypes: [{ code, label }, ...],
         templateVersions: [{ code, label }, ...]
       }
     }
============================================================================= */
export const fetchShopConfig = async () => {
  try {
    const res = await apiClient.get("/api/shop/products", {
      // Public endpoint; keeping withCredentials is harmless if your apiClient
      // is already configured with cookies/sessions.
      withCredentials: true,
    });
    return res.data;
  } catch (error) {
    console.error("[ShopService] Error fetching shop config:", getErrorMessage(error));
    throw error;
  }
};

/* =============================================================================
   2) createOrderAndInitPayment(payload)
   -----------------------------------------------------------------------------
   Creates an Order and initializes a Przelewy24 payment session.

   Backend:
     POST /api/shop/checkout    (shopController.createOrderAndInitPayment)

   Payload (frontend → backend):
     {
       amount: number,                 // major units (e.g. 150.00)
       buyerEmail: string,             // required
       buyerName?: string | null,
       recipientName?: string | null,
       recipientEmail?: string | null,
       message?: string | null,

       // Layout controls (CURRENT APP DEVELOPMENT):
       templateType?: "A4_ON_STATIONERY" | "A4_ENVELOPE" | "A4_COMBINED" | null,
       templateVersion?: "company" | "christmas" | "valentine" | null,

       // Optional legacy:
       // theme?: string | null
       // Backend will derive theme from templateVersion if theme missing.
     }

   Response:
     {
       orderId: number,
       redirectUrl: string  // P24 hosted payment URL
     }

   Frontend flow:
     - call createOrderAndInitPayment(payload)
     - redirect browser to redirectUrl
     - P24 returns to frontend urlReturn:
         /app/shop/thank-you?orderId=<id>&token=<sessionId>
============================================================================= */
export const createOrderAndInitPayment = async (payload) => {
  try {
    const res = await apiClient.post("/api/shop/checkout", payload, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (error) {
    console.error("[ShopService] Error creating order / P24 init:", getErrorMessage(error));
    throw error;
  }
};

/* =============================================================================
   3) getOrderStatus(orderId, token)
   -----------------------------------------------------------------------------
   Used by ThankYouPage to poll until the order becomes "paid" and voucher
   information becomes available.

   IMPORTANT (CURRENT BACKEND HARDENING):
   --------------------------------------
   This endpoint is PUBLIC and requires token:
     GET /api/shop/orders/:id?token=<token>

   The token is the Order.session_id generated on backend via crypto.randomUUID().
   The frontend must take it from URL query string on the Thank You page.

   Parameters:
     - orderId: number|string (required)
     - token:   string        (required)

   Example response (CURRENT):
     {
       id: 123,
       status: "pending" | "paid" | "error" | ...,
       voucherId: number | null,
       voucherNo: string | null,            // customer-facing voucher number
       voucher: { id, voucherNo, pdfUrl, publicToken } | null,

       // PII fields are now MASKED by backend:
       buyerEmail: "u***@d***.com" | null,
       buyerName: "J*** D***" | null,
       recipientName: "A***" | null,

       productCode: "VOUCHER_DYNAMIC",
       amount_minor_unit: 15000,
       amount: 150.00,
       currency: "PLN",
       paid_at: "2025-02-01T12:00:00Z" | null,

       // Layout info for UI consistency:
       theme: "default" | "christmas" | "valentine" | null,
       templateType: "A4_ON_STATIONERY" | ... | null,
       templateVersion: "company" | "christmas" | "valentine" | null,

       // Email delivery status (NO last_error in public response):
       email: {
         status: "pending" | "queued" | "sent" | "failed" | null,
         attempts: number | null,
         sent_at: string | null,
         queued_at: string | null
       }
     }

   Error behavior:
     - If token missing/mismatch OR order not found → 404 "Order not found."
============================================================================= */
export const getOrderStatus = async (orderId, token) => {
  try {
    if (orderId === undefined || orderId === null || `${orderId}`.trim() === "") {
      const err = new Error("getOrderStatus: orderId is required.");
      err.code = "SHOP_ORDER_ID_MISSING";
      throw err;
    }

    if (!token || `${token}`.trim() === "") {
      // Backend will return 404 for mismatch/missing token. We fail earlier to
      // avoid repeated polling calls that can never succeed.
      const err = new Error("getOrderStatus: token is required (from Thank You URL).");
      err.code = "SHOP_TOKEN_MISSING";
      throw err;
    }

    const url = buildOrderStatusUrl(orderId, token) + `&t=${Date.now()}`;

    const res = await apiClient.get(url, {
      withCredentials: true,
    });

    return res.data;
  } catch (error) {
    console.error(
      `[ShopService] Error fetching order status (orderId=${orderId}):`,
      getErrorMessage(error)
    );
    throw error;
  }
};

/* =============================================================================
   4) Convenience helpers (optional but practical)
============================================================================= */

/**
 * parseThankYouParams(locationSearch)
 *
 * Utility to parse the Thank You URL query string:
 *   ?orderId=123&token=shop_...uuid...
 *
 * You can use this in ThankYouPage:
 *   const { orderId, token } = parseThankYouParams(location.search);
 */
export const parseThankYouParams = (locationSearch) => {
  const params = new URLSearchParams(locationSearch || "");
  const orderId = params.get("orderId");
  const token = params.get("token");
  return {
    orderId: orderId ? String(orderId) : null,
    token: token ? String(token) : null,
  };
};

/**
 * pollOrderStatus({ orderId, token, intervalMs, timeoutMs, onTick })
 *
 * Optional polling helper for ThankYouPage.
 * - Avoids duplicating setInterval logic across components.
 * - Returns the final response when voucher is ready or when timeout occurs.
 *
 * Typical usage:
 *   const data = await pollOrderStatus({
 *     orderId,
 *     token,
 *     intervalMs: 2000,
 *     timeoutMs: 60000,
 *     onTick: (d) => setState(d)
 *   });
 *
 * Stop conditions (customize as needed):
 * - When status === "paid" and (voucherNo || voucherId) is present.
 */
export const pollOrderStatus = async ({
  orderId,
  token,
  intervalMs = 2000,
  timeoutMs = 60000,
  onTick = null,
}) => {
  const startedAt = Date.now();

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const data = await getOrderStatus(orderId, token);

    if (typeof onTick === "function") {
      try {
        onTick(data);
      } catch {
        // ignore UI callback errors
      }
    }

    // Stop when voucher is available OR when backend confirms paid and voucher object appears.
    const isPaid = String(data?.status || "").toLowerCase() === "paid";
    const hasVoucher = Boolean(data?.voucherNo || data?.voucherId || data?.voucher?.voucherNo);

    if (isPaid && hasVoucher) return data;

    if (Date.now() - startedAt >= timeoutMs) {
      const err = new Error("Polling timeout: voucher not ready yet.");
      err.code = "SHOP_POLL_TIMEOUT";
      err.last = data;
      throw err;
    }

    await new Promise((r) => setTimeout(r, intervalMs));
  }
};

/* =============================================================================
   Default export (like voucherService)
============================================================================= */
export default {
  fetchShopConfig,
  createOrderAndInitPayment,
  getOrderStatus,
  parseThankYouParams,
  pollOrderStatus,
};
